import React, { useEffect, useRef, useState } from 'react';
import { Clock, ThumbsUp, Briefcase } from 'lucide-react';

const JobSeekerServices = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const services = [
    {
      icon: Clock,
      title: 'Work When You Want',
      description: 'You tell us when you want to work, and how much. Endless opportunities await you across British Columbia.',
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      icon: ThumbsUp,
      title: 'Referrals',
      description: 'Work with diligence and integrity and WorkNation will reward you with solid referrals and ongoing opportunities.',
      gradient: 'from-indigo-500 to-indigo-600',
    },
    {
      icon: Briefcase,
      title: 'Permanent Placements',
      description: 'Looking for something long-term? We help connect workers with permanent positions. It\'s your turn to find your career.',
      gradient: 'from-blue-600 to-indigo-600',
    },
  ];

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-white relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-blue-50 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-float"></div>
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-indigo-50 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-float" style={{animationDelay: '2s'}}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Our Services For Job Seekers</span>
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Work when and how often you want. It's up to you. Let us help you find employment by introducing you to some of the busiest companies across BC.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className={`group bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-xl transition-all duration-700 hover:shadow-2xl hover:transform hover:scale-105 hover:-translate-y-2 border-t-4 border-[#003366] relative overflow-hidden ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150 + 200}ms` }}
            >
              {/* Animated gradient overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
              
              {/* Icon with animated gradient */}
              <div className={`relative inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 shadow-lg group-hover:shadow-2xl`}>
                <service.icon size={28} className="text-white" />
              </div>
              
              <h3 className="text-xl font-bold text-[#003366] mb-3 group-hover:text-[#004488] transition-colors duration-300 relative z-10">{service.title}</h3>
              <p className="text-gray-700 leading-relaxed relative z-10">{service.description}</p>
              
              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-100 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-bl-full"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default JobSeekerServices;